export type Theme = 'light' | 'dark'
export type ThemePreference = 'system' | Theme

export interface ThemeResolution {
  theme: Theme
  followsSystem: boolean
}

const COOKIE_NAME = 'constituent_theme'
const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)'

export function readStoredThemePreference(): Theme | null {
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]*)`),
    )
    if (match) {
      const value = match[1] as Theme
      if (value === 'light' || value === 'dark') return value
    }
  } catch {
    // Inaccessible storage behaves like an unset preference.
  }
  return null
}

export function resolveThemePreference(): ThemeResolution {
  const stored = readStoredThemePreference()
  if (stored) return { theme: stored, followsSystem: false }

  try {
    if (typeof window.matchMedia !== 'function') {
      return { theme: 'light', followsSystem: true }
    }
    return {
      theme: window.matchMedia(SYSTEM_DARK_QUERY).matches ? 'dark' : 'light',
      followsSystem: true,
    }
  } catch {
    return { theme: 'light', followsSystem: true }
  }
}

export function watchSystemTheme(onChange: (theme: Theme) => void): () => void {
  try {
    if (typeof window.matchMedia !== 'function') return () => {}
    const query = window.matchMedia(SYSTEM_DARK_QUERY)
    const handleChange = (event: MediaQueryListEvent) => {
      onChange(event.matches ? 'dark' : 'light')
    }
    query.addEventListener('change', handleChange)
    onChange(query.matches ? 'dark' : 'light')
    return () => query.removeEventListener('change', handleChange)
  } catch {
    return () => {}
  }
}

export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark')
  document.documentElement.style.colorScheme = theme
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'theme-color'
    document.head.appendChild(meta)
  }
  meta.content = theme === 'dark' ? '#0f172a' : '#2563eb'
}

export function saveThemePreference(theme: ThemePreference): void {
  if (theme === 'system') {
    const isSecure = window.location.protocol === 'https:'
    document.cookie = `${COOKIE_NAME}=; Path=/; SameSite=Lax; max-age=0${isSecure ? '; Secure' : ''}`
    applyTheme(resolveThemePreference().theme)
    return
  }
  applyTheme(theme)
  const isSecure = window.location.protocol === 'https:'
  const cookie = `${COOKIE_NAME}=${theme}; Path=/; SameSite=Lax; max-age=31536000${isSecure ? '; Secure' : ''}`
  document.cookie = cookie
}
